import { makeStringDataField, makeInt64DataField, logNotification } from '@microsoft/oteljs';
import { ARIA_OFFICE_TELEMETRY_TOKEN } from './InternalUtils';
var THROTTLE_EVENT_NAME = 'Office.Telemetry.OTelJS.EventThrottled';
function isNumeric(item) {
    return typeof item === 'number';
}
/**
 * Limits the number of telemetry events sent per second by a single event,
 * all events, and specific events
 */
var EventThrottler = /** @class */ (function () {
    /**
     * Constructs an Event Throttler for a 1DS Sink -- Allows a maximum number of events per second to be set for
     * a process, single event, and specific event
     * @param sinkName - The name of the 1DS Sink
     * @param telemetrySink - Telemetry Sink instance (used to send event throttled events)
     */
    function EventThrottler(_sinkName, _telemetrySink) {
        this._sinkName = _sinkName;
        this._telemetrySink = _telemetrySink;
        /**
         * The name of the preprocessor
         */
        this.name = 'EventThrottler';
        this._totalEventsInLastInterval = 0;
        this._singleEventInLastInterval = {};
        // Whether a throttle for this event name has been reported to Office.Telemetry in this session.
        this._eventThrottleReported = {};
        this._lastIntervalStart = Date.now();
        this._maxNamedEventsPerSecond = {};
    }
    /**
     * Create a throttle to limit the number of events per second a single event can send
     * @param eventsPerSecond - Maximum number of events per second that a single event can send.
     * If 0 or undefined, the throttle is not applied.
     */
    EventThrottler.prototype.setSingleEventThrottle = function (eventsPerSecond) {
        this._maxSingleEventPerSecond = eventsPerSecond;
    };
    /**
     * Create a throttle to limit the total number of events per second
     * @param eventsPerSecond - Maximum number of all events that can be sent per second.
     * If 0 or undefined, the throttle is not applied.
     */
    EventThrottler.prototype.setTotalEventThrottle = function (eventsPerSecond) {
        this._maxTotalEventsPerSecond = eventsPerSecond;
    };
    /**
     * Create a throttle for a specific event to limit the number of events per second
     * @param eventName - Name of event to be throttled
     * @param eventsPerSecond - Maximum number of this event per second. If 0, this event is suppressed completely.
     */
    EventThrottler.prototype.setNamedEventThrottle = function (eventName, eventsPerSecond) {
        this._maxNamedEventsPerSecond[eventName] = eventsPerSecond;
    };
    /**
     * Determine whether an event can be sent. Generally returns true; returns
     * false and logs an info message if an event is invalid or throttled.
     * @param telemetryEvent - An event to process
     */
    EventThrottler.prototype.processEvent = function (telemetryEvent) {
        var eventName = telemetryEvent.eventName;
        // Don't throttle the throttled event telemetry
        if (eventName === THROTTLE_EVENT_NAME) {
            return true;
        }
        var elapsedInterval = Date.now() - this._lastIntervalStart;
        var eventSuccess = true;
        if (elapsedInterval > 1000 /* milliseconds */) {
            this._lastIntervalStart = Date.now();
            this._totalEventsInLastInterval = 0;
            this._singleEventInLastInterval = {};
        }
        // Increment the counter per-event-name
        this._singleEventInLastInterval[eventName] = this._singleEventInLastInterval[eventName] + 1 || 1;
        if (eventName in this._maxNamedEventsPerSecond) {
            // Check for specifically named events
            eventSuccess = this.applyThrottle(this._singleEventInLastInterval[eventName], this._maxNamedEventsPerSecond[eventName], 'MaxNamedEventsPerSecond', eventName);
        }
        else if (isNumeric(this._maxSingleEventPerSecond)) {
            // Check if we've exceeded the limit for any single event
            eventSuccess = this.applyThrottle(this._singleEventInLastInterval[eventName], this._maxSingleEventPerSecond, 'MaxSingleEventsPerSecond', eventName);
        }
        // Check for the total events per second limit
        // For a small perf impact, we only increment totalEventsInLastInterval if we're actually interested in it
        if (isNumeric(this._maxTotalEventsPerSecond)) {
            this._totalEventsInLastInterval++;
            if (eventSuccess) {
                eventSuccess = this.applyThrottle(this._totalEventsInLastInterval, this._maxTotalEventsPerSecond, 'MaxTotalEventsPerSecond', eventName);
            }
        }
        return eventSuccess;
    };
    /**
     * Applies a throttle
     * Returns true if the event can be sent
     * Returns false and sends throttled telemetry event otherwise
     * @param eventCount - The number of events seen so far
     * @param throttleLimit - The number of events at which the throttle should be applied
     * @param throttleName - The name of the throttle
     * @param eventName - The name of the event
     */
    EventThrottler.prototype.applyThrottle = function (eventCount, throttleLimit, throttleName, eventName) {
        if (eventCount > throttleLimit) {
            // On the first throttled event for this category of throttling and/or this event name, send a telemetry event
            if (eventCount === throttleLimit + 1 && this._telemetrySink && !this._eventThrottleReported[eventName]) {
                this._eventThrottleReported[eventName] = true;
                this._telemetrySink.sendTelemetryEvent({
                    eventName: THROTTLE_EVENT_NAME,
                    telemetryProperties: {
                        ariaTenantToken: ARIA_OFFICE_TELEMETRY_TOKEN,
                        nexusTenantToken: 1723
                    },
                    eventFlags: {
                        diagnosticLevel: 110 /* DiagnosticLevel.RequiredServiceData */
                    },
                    dataFields: [
                        makeStringDataField('SinkName', this._sinkName || ''),
                        makeStringDataField('ThrottledEvent.Name', eventName),
                        makeInt64DataField(throttleName, throttleLimit)
                    ]
                });
            }
            logNotification(2 /* LogLevel.Info */, 1 /* Category.Sink */, function () {
                return "1DS Sink Event Throttle: Exceeded ".concat(throttleLimit, " ").concat(throttleName, ": ").concat(eventName);
            });
            return false;
        }
        return true;
    };
    return EventThrottler;
}());
export { EventThrottler };
//# sourceMappingURL=EventThrottler.js.map